"use client";

import { useRef, useState, useTransition } from "react";
import Link from "next/link";
import { AlertCircle, Eye, EyeOff, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AltchaField } from "@/components/auth/altcha-field";
import { loginAction } from "@/lib/api/auth-actions";

export function LoginForm({
  defaultEmail,
  defaultSlug,
}: {
  defaultEmail?: string;
  defaultSlug?: string;
} = {}) {
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pending, startTransition] = useTransition();
  const [captchaRequired, setCaptchaRequired] = useState(false);
  // Remount counter: a fresh key forces the widget to fetch and solve a new
  // challenge (each payload is consumed server-side after one attempt).
  const [captchaKey, setCaptchaKey] = useState(0);
  const altchaPayload = useRef<string | null>(null);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const email = (form.elements.namedItem("email") as HTMLInputElement).value;
    const password = (form.elements.namedItem("password") as HTMLInputElement)
      .value;
    const tenantSlug = (
      form.elements.namedItem("tenantSlug") as HTMLInputElement
    ).value;
    setError(null);
    // A successful login redirects server-side (the action throws NEXT_REDIRECT,
    // which the transition lets the router handle); only an error result returns.
    startTransition(async () => {
      const res = await loginAction(
        email,
        password,
        remember,
        tenantSlug,
        altchaPayload.current ?? undefined,
      );
      setError(res.error);
      // One payload authorises exactly one attempt server-side, so ANY
      // failure while the widget is (or becomes) visible spends it — remount
      // the widget for a fresh auto-solved challenge instead of letting the
      // next submit bounce on a consumed payload.
      if (res.captchaRequired || captchaRequired) {
        setCaptchaRequired(true);
        altchaPayload.current = null;
        setCaptchaKey((k) => k + 1);
      }
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="email">Adres e-mail</Label>
        <Input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          placeholder="jan.kowalski@firma.pl"
          defaultValue={defaultEmail}
          required
          className="h-11"
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="password">Hasło</Label>
          <Link
            href="/przypomnij-haslo"
            className="text-xs font-medium text-primary underline-offset-4 hover:underline"
          >
            Nie pamiętasz hasła?
          </Link>
        </div>
        <div className="relative">
          <Input
            id="password"
            name="password"
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            placeholder="••••••••"
            required
            className="h-11 pr-10"
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? "Ukryj hasło" : "Pokaż hasło"}
            className="absolute inset-y-0 right-0 flex w-10 cursor-pointer items-center justify-center text-muted-foreground transition-colors duration-200 hover:text-foreground"
          >
            {showPassword ? (
              <EyeOff className="size-4" />
            ) : (
              <Eye className="size-4" />
            )}
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="tenantSlug">Firma (opcjonalnie)</Label>
        {/* Field holds a URL-style slug, not an organization display name —
            autofill would inject a mismatching value and silently break login. */}
        <Input
          id="tenantSlug"
          name="tenantSlug"
          type="text"
          autoComplete="off"
          placeholder="identyfikator firmy"
          defaultValue={defaultSlug}
          className="h-11"
        />
      </div>

      <div className="flex items-center gap-2">
        <Checkbox
          id="remember"
          checked={remember}
          onCheckedChange={(value) => setRemember(Boolean(value))}
        />
        <Label htmlFor="remember" className="cursor-pointer font-normal">
          Zapamiętaj mnie na tym urządzeniu
        </Label>
      </div>

      {error ? (
        <div
          role="alert"
          className="flex items-start gap-2.5 rounded-xl border border-destructive/20 bg-destructive/8 px-3.5 py-3 text-sm text-destructive"
        >
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <span>{error}</span>
        </div>
      ) : null}

      {captchaRequired ? (
        <AltchaField
          key={captchaKey}
          onVerified={(payload) => {
            altchaPayload.current = payload;
          }}
        />
      ) : null}

      <Button
        type="submit"
        size="lg"
        disabled={pending}
        className="h-11 w-full gap-2 rounded-xl text-sm transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] active:scale-[0.99]"
      >
        {pending ? <Loader2 className="size-4 animate-spin" /> : null}
        {pending ? "Logowanie…" : "Zaloguj się"}
      </Button>
    </form>
  );
}
